import React, { useEffect, useState } from "react";
import { RefreshCw, Users, TrendingUp, Building2, Truck, Mail } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { listLeads, updateLeadStatus, getLeadStats } from "@/lib/api";

const INTEREST_LABELS = {
    investor: "Investor",
    franchise: "Franchise",
    destination_partner: "Destination",
    fleet: "Fleet",
    general: "General",
};

const STATUS_COLORS = {
    new: "bg-emerald-100 text-emerald-800 border-emerald-200",
    contacted: "bg-blue-100 text-blue-800 border-blue-200",
    qualified: "bg-amber-100 text-amber-800 border-amber-200",
    closed: "bg-stone-200 text-stone-700 border-stone-300",
};

export const Admin = () => {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = filter === "all" ? {} : { interest: filter };
            const [l, s] = await Promise.all([listLeads(params), getLeadStats()]);
            setLeads(l);
            setStats(s);
        } catch (e) {
            toast.error("Failed to load leads");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const changeStatus = async (id, status) => {
        try {
            await updateLeadStatus(id, status);
            toast.success("Status updated");
            load();
        } catch {
            toast.error("Failed to update status");
        }
    };

    return (
        <div className="min-h-screen bg-stone-50" data-testid="admin-page">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
                <div className="flex items-start justify-between mb-12 gap-6 flex-wrap">
                    <div>
                        <div className="e3-overline mb-3">Admin</div>
                        <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tighter text-stone-900">
                            Lead Pipeline
                        </h1>
                    </div>
                    <button onClick={load} className="e3-btn-ghost" data-testid="admin-refresh">
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-12">
                        <StatCard icon={Users} label="Total" value={stats.total} testid="admin-stat-total" />
                        <StatCard icon={TrendingUp} label="Investor" value={stats.by_interest.investor} testid="admin-stat-investor" />
                        <StatCard icon={Building2} label="Franchise" value={stats.by_interest.franchise} testid="admin-stat-franchise" />
                        <StatCard icon={Building2} label="Destination" value={stats.by_interest.destination_partner} testid="admin-stat-destination" />
                        <StatCard icon={Truck} label="Fleet" value={stats.by_interest.fleet} testid="admin-stat-fleet" />
                        <StatCard icon={Mail} label="General" value={stats.by_interest.general} testid="admin-stat-general" />
                    </div>
                )}

                {/* Filter */}
                <div className="mb-6 flex items-center gap-4">
                    <label className="e3-overline">Filter by interest</label>
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-56" data-testid="admin-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All leads</SelectItem>
                            <SelectItem value="investor">Investor</SelectItem>
                            <SelectItem value="franchise">Franchise</SelectItem>
                            <SelectItem value="destination_partner">Destination Partner</SelectItem>
                            <SelectItem value="fleet">Fleet</SelectItem>
                            <SelectItem value="general">General</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Table */}
                <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white">
                    <Table data-testid="admin-leads-table">
                        <TableHeader>
                            <TableRow className="bg-stone-100 hover:bg-stone-100">
                                <TableHead className="font-semibold text-stone-700 py-4">Name</TableHead>
                                <TableHead className="font-semibold text-stone-700">Contact</TableHead>
                                <TableHead className="font-semibold text-stone-700">Interest</TableHead>
                                <TableHead className="font-semibold text-stone-700">Location</TableHead>
                                <TableHead className="font-semibold text-stone-700">Message</TableHead>
                                <TableHead className="font-semibold text-stone-700">Status</TableHead>
                                <TableHead className="font-semibold text-stone-700">Received</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow><TableCell colSpan={7} className="text-center py-12 text-stone-500">Loading…</TableCell></TableRow>
                            )}
                            {!loading && leads.length === 0 && (
                                <TableRow><TableCell colSpan={7} className="text-center py-12 text-stone-500">No leads yet.</TableCell></TableRow>
                            )}
                            {leads.map((l) => (
                                <TableRow key={l.id} className="border-stone-200 hover:bg-stone-50">
                                    <TableCell className="font-medium text-stone-900 py-5">{l.name}</TableCell>
                                    <TableCell className="text-sm">
                                        <div className="text-stone-800">{l.email}</div>
                                        <div className="text-stone-500">{l.phone}</div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="border-stone-300">
                                            {INTEREST_LABELS[l.interest] || l.interest}
                                        </Badge>
                                        {l.stations_interest && (
                                            <div className="text-xs text-stone-500 mt-1">{l.stations_interest} stations</div>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-sm text-stone-600">{l.city || "—"}</TableCell>
                                    <TableCell className="text-sm text-stone-600 max-w-xs">
                                        <div className="line-clamp-2">{l.message || <span className="text-stone-400 italic">No message</span>}</div>
                                        <div className="text-[10px] text-stone-400 mt-1 uppercase tracking-wider">via {l.source_page}</div>
                                    </TableCell>
                                    <TableCell>
                                        <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                                            <SelectTrigger className={`w-32 h-8 text-xs ${STATUS_COLORS[l.status] || ""}`} data-testid={`admin-status-${l.id}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="new">New</SelectItem>
                                                <SelectItem value="contacted">Contacted</SelectItem>
                                                <SelectItem value="qualified">Qualified</SelectItem>
                                                <SelectItem value="closed">Closed</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    <TableCell className="text-xs text-stone-500">
                                        {new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
    <div className="e3-card-static !p-5" data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
            <div className="e3-overline">{label}</div>
            <Icon className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
        </div>
        <div className="font-serif text-3xl font-light text-stone-900">{value}</div>
    </div>
);
